import React, { useMemo } from "react";
import { Button } from "react-bootstrap";
import { Gpt } from "../base/Gpt";
import { MISSION_CONFIG } from "./missionConfig";

function styledTitle(topLabel, subtitle) {
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1.2 }}>
      <span style={{ fontStyle: "italic", fontSize: "0.7em", opacity: 0.7, whiteSpace: "nowrap" }}>{topLabel}</span>
      <span>{subtitle}</span>
    </span>
  );
}

export default function MissionGpt(props) {
  const missionName = props.missionData ? props.missionData.name : 'Unknown Mission';

  const missionGptConfig = useMemo(() => {
    const base = MISSION_CONFIG.gpt;
    const originalGetPrompts = base.getPrompts;
    const basePostVideo = base.postVideoPrompts || [];
    return {
      ...base,
      filmIdeas: [missionName],
      postVideoPrompts: basePostVideo.map(pvp => {
        if (pvp.title === "Crew")
          return { ...pvp, promptVal: [`Describe the mission team for the ${missionName} in detail.`] };
        if (pvp.title === "Training")
          return { ...pvp, promptVal: [`Create a detailed training plan for the ${missionName} mission. If the mission is crewed, include crew physical fitness requirements, spacecraft systems training, mission-specific simulations, emergency procedures, and EVA preparation. If the mission is uncrewed, include ground team training, remote operations procedures, autonomous systems testing, and contingency response drills. Include a training timeline from team selection to launch.`] };
        if (pvp.title === "Parts List")
          return { ...pvp, promptVal: [`Create a complete parts list for the ${missionName} next generation spacecraft design. Include all major systems: propulsion, life support, power, communications, navigation, thermal control, and structural components. For each part, list the component name, quantity, specification, cost, and supplier.`] };
        return pvp;
      }),
      getPrompts: (args) => {
        const prompts = originalGetPrompts(args);
        prompts[0] = {
          ...prompts[0],
          title: styledTitle(missionName, "Mission Overview"),
          showAfter: (
            <>
              <hr />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ backgroundColor: "white", fontSize: "large", padding: "10px 15px 15px 15px", borderRadius: "15px", border: "2px solid #f05f40", boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)" }}>
                  <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>Instructions:</h3>
                  <p style={{ margin: "0" }}>
                    <span style={{ fontWeight: "bold", fontSize: "1.1em" }}>Let's see what AI thinks the <span style={{ color: "#f05f40" }}>{missionName}</span> spacecraft looks like.</span>
                    <br /><br />
                    Use the{" "}
                    <Button variant="outline-primary" size="sm" onClick={() => args.handlePromptButtonClicked(null, 1)} style={{ margin: "0 5px", padding: "4px 12px", fontSize: "14px", display: "inline-block", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.3s ease" }}>
                      2: Mission Image
                    </Button>{" "}
                    prompt to create a detailed image description for an image generation model.
                  </p>
                </div>
              </div>
              <hr />
            </>
          ),
        };
        prompts[1] = { ...prompts[1], title: styledTitle(missionName, "Mission Image"), fillIn: missionName, noAutoAdvance: true, autoPopulateDalle: true };
        prompts[2] = {
          ...prompts[2],
          title: styledTitle(missionName, <span style={{ whiteSpace: "nowrap" }}>CAPCOM Transmission</span>),
          fillIn: missionName,
          showAfter: (
            <>
              <hr />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ backgroundColor: "white", fontSize: "large", padding: "10px 15px 15px 15px", borderRadius: "15px", border: "2px solid #f05f40", boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)" }}>
                  <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>Instructions:</h3>
                  <p style={{ margin: "0" }}>
                    Now bring your transmission to life. Select{" "}
                    <Button variant="outline-primary" size="sm" onClick={() => args.setActiveTab("voice-cloning")} style={{ margin: "0 5px", padding: "4px 12px", fontSize: "14px", display: "inline-block", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.3s ease", borderColor: "#f05f40", color: "#f05f40" }}>
                      Voice
                    </Button>{" "}
                    to turn your script into realistic spoken audio.
                  </p>
                </div>
              </div>
              <hr />
            </>
          ),
        };
        const hl = 'style="background-color: #f4e4bc; padding: 0 2px; border-radius: 2px"';
        prompts[3] = {
          ...prompts[3],
          title: styledTitle("Next Gen", "Mission Design"),
          fillIn: "",
          promptVal: [
            `Create a Next Generation <span ${hl}>${missionName}</span> mission design. Update the mission objectives, technologies, and systems while staying consistent with the mission's original purpose. Describe the design as it would exist in the year <span ${hl}>2026</span>.`,
          ],
          showAfter: (
            <>
              <hr />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ backgroundColor: "white", fontSize: "large", padding: "10px 15px 15px 15px", borderRadius: "15px", border: "2px solid #f05f40", boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)" }}>
                  <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>Instructions:</h3>
                  <p style={{ margin: "0" }}>
                    Select{" "}
                    <Button variant="outline-primary" size="sm" onClick={() => args.handlePromptButtonClicked(null, 4)} style={{ margin: "0 5px", padding: "4px 12px", fontSize: "14px", display: "inline-block", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.3s ease" }}>
                      5: Spacecraft Image
                    </Button>{" "}
                    to generate a detailed image description of your next generation spacecraft.
                  </p>
                </div>
              </div>
              <hr />
            </>
          ),
        };
        prompts[4] = {
          ...prompts[4],
          title: styledTitle("Next Gen", "Spacecraft Image"),
          fillIn: missionName,
          autoPopulateDalle: true,
          promptVal: [
            "Write an AI image prompt for a Next Gen ",
            " spacecraft that displays the upgraded technology clearly through realistic, visible hardware details. Use a simple space background showing the craft actively during the mission. Style: documentary photo, sharp focus, natural lighting, photorealistic, accurate materials, scale, and engineering realism. No text, labels, logos, or watermarks.",
          ],
          showAfter: (
            <>
              <hr />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ backgroundColor: "white", fontSize: "large", padding: "10px 15px 15px 15px", borderRadius: "15px", border: "2px solid #f05f40", boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)" }}>
                  <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>Instructions:</h3>
                  <p style={{ margin: "0" }}>
                    Now generate an image of your <em>Next Generation</em> <span style={{ color: "#f05f40", fontWeight: "bold" }}>{missionName}</span> mission.
                    <br /><br />
                    Select{" "}
                    <Button variant="outline-primary" size="sm" onClick={() => args.setActiveTab("dalle")} style={{ margin: "0 5px", padding: "4px 12px", fontSize: "14px", display: "inline-block", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.3s ease", borderColor: "#f05f40", color: "#f05f40" }}>
                      Image
                    </Button>
                  </p>
                </div>
              </div>
              <hr />
            </>
          ),
        };
        return prompts;
      },
    };
  }, [missionName]);

  return (
    <Gpt
      {...props}
      workshopConfig={missionGptConfig}
      storageKey={MISSION_CONFIG.storageKeys.gptMessages}
    />
  );
}
